// SSE event types matching backend
export type SSEEvent =
  | { event: 'text'; data: { content: string } }
  | { event: 'tool_start'; data: { tool: string; id: string } }
  | { event: 'tool_end'; data: { id: string } }
  | { event: 'ui_action'; data: UIActionPayload }
  | { event: 'done'; data: { conversationId: string } }
  | { event: 'error'; data: { message: string } }

export type UIActionPayload =
  | { action: 'role_select'; options: { label: string; description: string }[] }
  | {
      action: 'confirm_info'
      sections: { title: string; fields: { label: string; value: string }[] }[]
    }
  | { action: 'multi_select'; options: string[]; label: string }
  | { action: 'identity_prompt' }
  | { action: 'complete_onboarding'; data: Record<string, unknown> }

const BASE_URL = import.meta.env.VITE_ASSISTANT_URL

async function* parseSSEStream(response: Response): AsyncGenerator<SSEEvent> {
  if (!response.body) throw new Error('Response body is null')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let currentEvent = ''
  let currentData = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7)
      } else if (line.startsWith('data: ')) {
        currentData = line.slice(6)
      } else if (line === '') {
        if (currentEvent && currentData) {
          try {
            yield { event: currentEvent, data: JSON.parse(currentData) } as SSEEvent
          } catch {
            // skip malformed events
          }
        }
        currentEvent = ''
        currentData = ''
      }
    }
  }
}

export interface InitialContext {
  origin: 'direct_signup' | 'invitation_link' | 'data_room'
  token?: string
}

export async function* sendMessage(
  message: string,
  conversationId?: string,
  initialContext?: InitialContext
): AsyncGenerator<SSEEvent> {
  const response = await fetch(`${BASE_URL}/api/v1/chat/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ message, conversationId, initialContext }),
  })

  if (!response.ok) {
    throw new Error(`Chat API error: ${response.status}`)
  }

  yield* parseSSEStream(response)
}

export async function fetchActiveConversation(): Promise<{
  conversationId: string
  uiMessages: unknown[]
} | null> {
  const response = await fetch(`${BASE_URL}/api/v1/chat/conversations/active`, {
    credentials: 'include',
  })

  if (response.status === 404) return null
  if (!response.ok) throw new Error(`Fetch conversation error: ${response.status}`)

  return response.json()
}

export async function saveUiMessages(conversationId: string, messages: unknown[]): Promise<void> {
  await fetch(`${BASE_URL}/api/v1/chat/conversations/${conversationId}/ui-messages`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ messages }),
  })
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await fetch(`${BASE_URL}/api/v1/chat/conversations/${conversationId}`, {
    method: 'DELETE',
    credentials: 'include',
  })
}

export async function* sendAction(
  conversationId: string,
  action: { type: string; data: Record<string, unknown> }
): AsyncGenerator<SSEEvent> {
  const response = await fetch(`${BASE_URL}/api/v1/chat/messages/${conversationId}/action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(action),
  })

  if (!response.ok) {
    throw new Error(`Chat action API error: ${response.status}`)
  }

  yield* parseSSEStream(response)
}
